import { authSession } from '@/shared/api'
import { env } from '@/shared/config'
import { runResets } from '@/shared/lib/reset-registry'

import { changePassword as requestPasswordChange, revokeAllSessions } from '../api/account-api'
import {
  endProviderSession,
  requestPasswordToken,
  requestRefreshedToken,
  requestRegistration,
  type RegistrationFailure,
  type TokenFailure,
} from '../api/token-api'

import {
  clearCredential,
  getIdentifier,
  getRefreshToken,
  storeCredential,
} from './credential-store'

/** Honoured in development builds only — mirrors the check in `env.ts`. */
export const isAuthDisabled = env.isDev && env.VITE_AUTH_DISABLED

export type SignInResult = { ok: true } | { ok: false; reason: TokenFailure }

export type RegisterResult = { ok: true } | { ok: false; reason: RegistrationFailure }

let ready: Promise<void> | null = null

/**
 * Fires when a credential first arrives — after sign-in, and after a reload
 * exchanges a stored refresh token. Anything that needs a token to start (the
 * stream) subscribes instead of polling.
 */
const signedInHandlers = new Set<() => void>()

export function onSignedIn(handler: () => void): () => void {
  signedInHandlers.add(handler)
  return () => signedInHandlers.delete(handler)
}

const announceSignedIn = () => {
  for (const handler of signedInHandlers) handler()
}

/**
 * A reload keeps the refresh token but not the access token, so the first
 * thing the guard needs is one exchange. Async, and the router's first
 * `beforeLoad` starts before it can finish — so consumers await this promise
 * instead of racing it. Created once and reused.
 */
export function ensureAuthReady(): Promise<void> {
  if (isAuthDisabled) return Promise.resolve()

  ready ??= restore()
  return ready
}

async function restore(): Promise<void> {
  if (authSession.getToken() !== null) return

  const refreshToken = getRefreshToken()
  if (refreshToken === null) return

  const result = await requestRefreshedToken(refreshToken)

  if (result.ok) {
    storeCredential(result.tokens)
    announceSignedIn()
    return
  }

  // A refresh token the provider no longer honours is simply "not signed in".
  clearCredential()
}

export async function signIn(username: string, password: string): Promise<SignInResult> {
  const result = await requestPasswordToken(username, password)

  if (result.ok) {
    storeCredential(result.tokens)
    // The boot exchange is moot now, and leaving it unset would make a later
    // `ensureAuthReady()` start one against a refresh token already spent.
    ready = Promise.resolve()
    announceSignedIn()
    return { ok: true }
  }

  // The provider answers 401 identically for an unknown login and a wrong
  // password — deliberately, down to the timing — so `credentials` covers both.
  return { ok: false, reason: result.reason }
}

/**
 * Registration is a sign-in: goauth answers with a token pair, so the new
 * account is already through the door and no password grant follows.
 */
export async function register(username: string, password: string): Promise<RegisterResult> {
  const result = await requestRegistration(username, password)

  if (!result.ok) return { ok: false, reason: result.reason }

  storeCredential(result.tokens)
  ready = Promise.resolve()
  announceSignedIn()
  return { ok: true }
}

/**
 * Changing a password revokes every session, so the fresh pair that comes back
 * is what keeps this tab signed in. It also clears `must_change_password`,
 * which is why the reply's omitted flag is read as `false`.
 */
export async function changePassword(current: string, next: string): Promise<void> {
  storeCredential(await requestPasswordChange(current, next))
}

/**
 * Ends every session including this one, so there is no signed-in state left to
 * return to — the local teardown is not a courtesy, it is the truth.
 */
export async function signOutEverywhere(): Promise<void> {
  await revokeAllSessions()
  runResets()
}

/**
 * Re-runs the password grant to satisfy goauth's sudo window: `auth_time` is
 * only reset by a password, never by a refresh, and there is no dedicated
 * re-authentication endpoint.
 *
 * Note this opens a *new* session family rather than replacing the current one,
 * so an extra row appears on the sessions page. That is goauth's behaviour.
 */
export async function reauthenticate(password: string): Promise<SignInResult> {
  const identifier = getIdentifier()
  if (identifier === null) return { ok: false, reason: 'credentials' }

  const result = await requestPasswordToken(identifier, password)
  if (!result.ok) return { ok: false, reason: result.reason }

  storeCredential(result.tokens)
  return { ok: true }
}

/**
 * Obtains a fresh access token for the 401 interceptor. Returns the shape
 * `shared/api` expects, so the transport never learns what a provider is.
 */
export async function refreshCredential(): Promise<{ ok: true; token: string } | { ok: false }> {
  const refreshToken = getRefreshToken()
  if (refreshToken === null) return { ok: false }

  const result = await requestRefreshedToken(refreshToken)
  if (!result.ok) return { ok: false }

  storeCredential(result.tokens)
  return { ok: true, token: result.tokens.access_token }
}

export const isSignedIn = () => !isAuthDisabled && authSession.getToken() !== null

/**
 * One sign-out for both causes — the button and a refresh that failed. It ends
 * the provider session, then clears the query cache, every store, the stream
 * connection and both tokens. Navigation stays at the call site.
 */
export async function signOut(): Promise<void> {
  // Before `runResets()`: revoke reads the session id off the access token, and
  // the reset clears it.
  if (isSignedIn()) await endProviderSession()

  // `clearCredential` is registered as a reset, so this drops the tokens too.
  runResets()
}
